<template>
  <PageContainer
    as="header"
    class="app-header"
    :data-header-bg="route.meta.headerBg"
    :data-scrolled="isScrolled || undefined"
  >
    <CarrotInline justify="space-between" align="center">
      <router-link to="/" class="app-header__logo">
        <img :src="currentLogoSrc" alt="Carrot" class="app-header__logo-img" />
      </router-link>

      <CarrotMenu horizontal nav aria-label="Main" class="app-header__nav-desktop">
        <CarrotMenuItem
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :active="isActive(item.to)"
        >
          {{ item.label }}
        </CarrotMenuItem>
      </CarrotMenu>

      <button
        type="button"
        class="app-header__menu-toggle"
        aria-label="Open menu"
        @click="drawerOpen = true"
      >
        <LucideIconRender name="Menu" :size="24" />
      </button>
    </CarrotInline>

    <CarrotDrawer v-model:open="drawerOpen" side="right" size="md" aria-label="Mobile navigation">
      <CarrotDrawerHeader>Menu</CarrotDrawerHeader>
      <CarrotDrawerBody>
        <CarrotMenu aria-label="Mobile" @click="drawerOpen = false" size="lg">
          <CarrotMenuItem
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            :active="isActive(item.to)"
          >
            {{ item.label }}
          </CarrotMenuItem>
        </CarrotMenu>
      </CarrotDrawerBody>
      <CarrotDrawerFooter>
        <CarrotMenu aria-label="Legal" size="sm" @click="drawerOpen = false" horizontal>
          <CarrotMenuItem
            v-for="item in legalItems"
            :key="item.to"
            :to="item.to"
            :active="isActive(item.to)"
          >
            {{ item.label }}
          </CarrotMenuItem>
        </CarrotMenu>
      </CarrotDrawerFooter>
    </CarrotDrawer>
  </PageContainer>
</template>

<script setup lang="ts">
import PageContainer from "@/components/PageContainer.vue";
import logoSrc from "@/assets/logo/Logo.svg";
import logoOrangeSrc from "@/assets/logo/Logo-Orange.svg";
import {
  CarrotDrawer,
  CarrotDrawerBody,
  CarrotDrawerFooter,
  CarrotDrawerHeader,
  CarrotInline,
  CarrotMenu,
  CarrotMenuItem,
} from "@carrot/design-controls-vue";
import { LucideIconRender } from "@carrot/icons-lucide-vue";
import { useRoute } from "vue-router";
import { computed, onMounted, onUnmounted, ref } from "vue";

const route = useRoute();

const currentLogoSrc = computed(() =>
  route.meta.logoVariant === "orange" ? logoOrangeSrc : logoSrc,
);

const drawerOpen = ref(false);

const SCROLL_THRESHOLD = 10;
const isScrolled = ref(window.scrollY > SCROLL_THRESHOLD);

function updateScrolled(): void {
  isScrolled.value = window.scrollY > SCROLL_THRESHOLD;
}

onMounted(() => window.addEventListener("scroll", updateScrolled, { passive: true }));
onUnmounted(() => window.removeEventListener("scroll", updateScrolled));

const navItems = [
  { to: "/", label: "Home" },
  { to: "/product", label: "Our Product" },
  { to: "/technology", label: "Technology" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/support", label: "Support" },
];

const legalItems = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
];

function isActive(to: string): boolean {
  return to === "/" ? route.path === "/" : route.path.startsWith(to);
}
</script>

<style scoped>
@layer kapish.components {
  .app-header {
    position: sticky;
    top: 0;
    z-index: var(--z-sticky);
    background: transparent;
    transition: background-color var(--transition-duration, var(--duration-normal)) ease;
    padding-block: var(--space-4, 1rem);
  }

  .app-header[data-scrolled] {
    background: var(--surface-background);
  }

  .app-header[data-header-bg="secondary"][data-scrolled] {
    background: var(--color-secondary);
  }

  .app-header[data-header-bg="secondary"] .app-header__menu-toggle {
    color: var(--color-carrot--on-blue);
  }

  .app-header__logo {
    text-decoration: none;
    display: inline-flex;
  }

  .app-header__logo-img {
    height: 1.75rem;
    width: auto;
  }

  .app-header__nav-desktop {
    display: none;
  }

  /*
   * CarrotDrawerFooter defaults to justify-content: flex-end (built
   * for trailing action buttons) - left-align the legal menu instead
   * so it reads as a continuation of the main nav above, not a
   * right-aligned button row. 
   */
  .carrot-drawer-footer {
    justify-content: flex-start;
  }

  /*
   * :global() required: .carrot-drawer carries no data-v-* attribute
   * at all (confirmed live) - it's a node CarrotDrawer builds
   * internally, not slot content, so neither a plain selector nor
   * :deep() has anything to match against. Same situation as
   * CarrotModal's internal backdrop from the cookie-consent fix.
   * Library default is max-width: 100% - full-bleed on narrow phones;
   * cap it at 90% so a sliver of the backdrop stays visible/tappable.
   * !important: both rules land in the same kapish.components layer,
   * and the library's own rule happens to load after this one in the
   * final bundle, so equal specificity alone isn't enough to win.
   */
  :global(.carrot-drawer) {
    max-width: 90% !important;
  }

  .app-header__menu-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: var(--space-2, 0.5rem);
    margin: calc(var(--space-2, 0.5rem) * -1);
    cursor: pointer;
    color: inherit;
  }

  @container (min-width: 768px) {
    .app-header__nav-desktop {
      display: flex;
    }

    .app-header__menu-toggle {
      display: none;
    }
  }
}
</style>
